/**
 * Single source of truth for ALL tier display data — checkout, picker, and
 * any future tier-aware surface.
 *
 * Each tier has two sets of marketing copy:
 *   - Checkout fields (`name`, `description`, `benefits`, `benefitsPreamble`)
 *     used by CheckoutSlide.
 *   - Picker fields (`picker*`) used by PlanPickerModal cards.
 *
 * Every wire tier has an entry — including both free tiers — so this map
 * lines up one-to-one with `Plan`'s registry. Whether a tier is *offered*
 * (checkout, upgrade CTAs) is not a descriptor concern: ask
 * `Plan.from(tier).isPurchasable()`. The free entries exist so surfaces that
 * show the user's *current* plan (picker "Your current plan" card, sidenav
 * badge, upgrade modals) read the same copy source as paid tiers.
 *
 * BE wire strings remain the canonical discriminator; product names here
 * are display-only.
 */
import type { SubscriptionTier, TierRank } from "./tiers";

export interface TierDescriptor {
  /** BE wire string — unchanged regardless of display rename. */
  tier: SubscriptionTier;
  /**
   * Ordinal rank used for upgrade/downgrade comparison. Higher = more
   * expensive. Single source of truth for tier ordering — `isDowngrade`
   * and `getDowngradeCopy` in DowngradeConfirmModal consume this. Both free
   * tiers are rank 0.
   */
  rank: TierRank;
  /** Small uppercase eyebrow above the product name on cards + checkout. */
  tierOrdinal: string;
  /** Product display name (checkout). */
  name: string;
  /** Short tagline rendered under the price on the checkout slide. */
  description: string;
  /** Headline monthly price in cents. */
  monthlyPriceCents: number;
  /** Steady-state renewal price after a mid-cycle prorated upgrade. */
  renewalPriceCents: number;
  /** "Everything in <X>, plus:" preamble for the checkout benefits list. */
  benefitsPreamble: string | null;
  /** Benefits rendered on the checkout slide. */
  benefits: string[];
  /** Plan-name string sent to chatSlice via setSelectedPaymentPlanName. */
  planName: string;
  /** Consultations included per billing cycle; 0 for tiers without lawyer time. */
  monthlyTasks: number;

  // ─── Picker-specific fields ─────────────────────────────────────────
  /** Display name on the picker card. */
  pickerDisplayName: string;
  /** Tagline under the name on the picker card. */
  pickerDescription: string;
  /** "Everything in <X> and:" preamble above picker benefits. */
  pickerBenefitsDescription: string;
  /** Benefits shown on the picker card. */
  pickerBenefits: string[];
  /** CTA label on the picker card. */
  pickerCtaLabel: string;
  /** Whether the card gets a bold border + shadow. */
  pickerHighlight: boolean;
  /** Badge text ("MOST POPULAR", "BEST VALUE"). Empty = no badge. */
  pickerBadge: string;
  /** Icon key — mapped to JSX in PlanPickerModal. */
  pickerIconKey: "sparkle" | "sparkles" | "briefcase";
  /** Whether to show lawyer avatars next to the price. */
  pickerShowAvatars: boolean;
}

/**
 * Copy shared by both free tiers. `pre_onboarding` and `free_tier` are the
 * same plan at every gate (subscription-tier-model spec); only the wire value
 * differs, so each entry below overrides `tier`/`planName`.
 */
const FREE_COPY: Omit<TierDescriptor, "tier" | "planName"> = {
  rank: 0,
  tierOrdinal: "",
  name: "Inhouse Free",
  description: "When you just need quick legal advice",
  monthlyPriceCents: 0,
  renewalPriceCents: 0,
  benefitsPreamble: null,
  benefits: [],
  monthlyTasks: 0,

  pickerDisplayName: "Free",
  pickerDescription: "Try Inhouse on everyday legal questions.",
  pickerBenefitsDescription: "",
  pickerBenefits: [
    "AI guidance on everyday legal questions",
    "Confidential conversations with AI",
    "Secure document storage",
  ],
  pickerCtaLabel: "Your current plan",
  pickerHighlight: false,
  pickerBadge: "",
  pickerIconKey: "sparkle",
  pickerShowAvatars: false,
};

/** All tiers — keyed by BE wire value. */
export const TIER_DESCRIPTORS: Record<SubscriptionTier, TierDescriptor> = {
  pre_onboarding: { ...FREE_COPY, tier: "pre_onboarding", planName: "pre_onboarding" },
  free_tier: { ...FREE_COPY, tier: "free_tier", planName: "free_tier" },
  subscriber_business: {
    tier: "subscriber_business",
    rank: 1,
    tierOrdinal: "TIER I",
    name: "Inhouse AI",
    description: "Get AI legal advice and draft, review, or redline any document.",
    monthlyPriceCents: 4900,
    renewalPriceCents: 4900,
    benefitsPreamble: "What's included",
    benefits: [
      "Generate nuanced legal documents in minutes",
      "Instant guidance on any legal matter",
      "Member rates for legal work",
    ],
    planName: "subscriber_business",
    monthlyTasks: 0,

    pickerDisplayName: "Inhouse AI",
    pickerDescription: "Do your own legal work with expert AI.",
    pickerBenefitsDescription: "",
    pickerBenefits: [
      "$5,000/month of self-serve legal work",
      "Private data storage for chats and docs",
      "Accuracy Guarantee on your documents",
      "Lawyer support for lawsuits and letters",
      "Discounted legal services",
    ],
    pickerCtaLabel: "Upgrade to Inhouse AI",
    pickerHighlight: false,
    pickerBadge: "",
    pickerIconKey: "sparkles",
    pickerShowAvatars: false,
  },
  subscriber_enterprise: {
    tier: "subscriber_enterprise",
    rank: 2,
    tierOrdinal: "TIER II",
    name: "Inhouse Counsel",
    description: "Adds an hour of lawyer time to review and finalize your work.",
    monthlyPriceCents: 14900,
    renewalPriceCents: 14900,
    benefitsPreamble: "Everything in the AI plan, plus:",
    benefits: [
      "1-hour consultation with an elite lawyer monthly",
      "Privileged advice and legal documents from Inhouse AI",
      "Discounted member rates for legal work",
    ],
    planName: "subscriber_enterprise",
    monthlyTasks: 1,

    pickerDisplayName: "Inhouse Counsel",
    pickerDescription: "Do more legal work with a lawyer in your corner.",
    pickerBenefitsDescription: "Everything in Inhouse AI, plus:",
    pickerBenefits: [
      "1 lawyer consultation / month",
      "$15,000/month of self-serve legal work",
      "Attorney-client privileged chats",
    ],
    pickerCtaLabel: "Upgrade to Inhouse Counsel",
    pickerHighlight: true,
    pickerBadge: "Most popular",
    pickerIconKey: "briefcase",
    pickerShowAvatars: false,
  },
  inhouse_counsel: {
    tier: "inhouse_counsel",
    rank: 3,
    tierOrdinal: "TIER III",
    name: "Inhouse Counsel Plus",
    description: "Adds five hours of lawyer time to review and finalize your work.",
    monthlyPriceCents: 49900,
    renewalPriceCents: 49900,
    benefitsPreamble: "Everything in Tier II, plus:",
    benefits: [
      "5 x 1 hour with a real, licensed lawyer",
      "Get tailored advice from a licensed lawyer",
      "Conversations with AI are privileged",
      "Sign with confidence, a lawyer finalizes your documents",
    ],
    planName: "inhouse_counsel",
    monthlyTasks: 5,

    pickerDisplayName: "Inhouse Counsel Plus",
    pickerDescription: "When you need dedicated legal support",
    pickerBenefitsDescription: "Everything in Inhouse Counsel and:",
    pickerBenefits: [
      "5 x 1 hour with a real, licensed lawyer",
      "Get tailored advice from a licensed lawyer",
      "Sign with confidence, a lawyer finalizes your documents",
    ],
    pickerCtaLabel: "Upgrade",
    pickerHighlight: false,
    pickerBadge: "",
    pickerIconKey: "briefcase",
    pickerShowAvatars: false,
  },
};

/** "$49" / "$149" / "$350" — bare amount, no "/mo" suffix. */
export const formatTierPrice = (cents: number): string => {
  const dollars = cents / 100;
  return `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
};

/** "$49/mo" — bare amount + "/mo" suffix; used on the picker cards. */
export const formatTierMonthlyLabel = (descriptor: TierDescriptor): string =>
  `${formatTierPrice(descriptor.monthlyPriceCents)}/mo`;
